import { Info } from "lucide-react";
import React, { ComponentProps, ReactNode } from "react";

import type { Optional } from "@intelligems/ig-types";
import {
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  Stack,
  SxProps,
  TooltipProps,
  TypographyProps,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid";

import { IgIconComponent } from "./IgIconComponent";
import { IgTooltip } from "./IgTooltip";
import { IgTypography } from "./IgTypography";

// Create a separate component outside of the render function
const IconWithColorWrapper = (iconColor?: string) => {
  return (props: React.ComponentProps<typeof IgIconComponent>) => (
    <IgIconComponent {...props} color={iconColor} />
  );
};

type IgSelectProps<Value = unknown> = Omit<
  Optional<SelectProps<Value>, "variant">,
  "onChange"
> & {
  placeholder?: string;
  onChange?: IgSelectEventHandler<Value>;
  label?: string;
  labelColor?: string;
  orientation?: "horizontal" | "vertical";
  helperText?: string;
  tooltipMessage?: string | ReactNode;
  tooltipPlacement?: TooltipProps["placement"];
  labelProps?: TypographyProps;
  disabledTooltipMessage?: ReactNode;
  iconColor?: string;
  containerSx?: SxProps;
};

type SelectProps<Value> = ComponentProps<typeof Select<Value>>;
export type IgSelectEvent<Value = unknown> = Parameters<
  NonNullable<SelectProps<Value>["onChange"]>
>[0];
export type IgSelectEventHandler<Value> = NonNullable<
  SelectProps<Value>["onChange"]
>;

export const IgSelect = <Value = unknown,>(props: IgSelectProps<Value>) => {
  const {
    children,
    placeholder,
    sx,
    variant,
    onChange,
    label,
    orientation,
    helperText,
    tooltipMessage,
    tooltipPlacement = "bottom",
    labelProps,
    fullWidth,
    disabledTooltipMessage,
    iconColor,
    containerSx,
    required,
    // Forwarded verbatim, so the menu keeps MUI's default of portalling to
    // `document.body`. A `disablePortal` menu mounts inside the React root
    // instead, and the modal manager then aria-hides every child of `<body>`
    // that is not the menu's own mount node -- which is the whole app, the
    // dialog the select sits in, and the menu's own options along with it.
    MenuProps,
    ...rest
  } = props;

  const isHorizontal = orientation === "horizontal";
  const theme = useTheme();

  // Pass iconColor through to IgIconComponent's color prop when provided
  const IconWithColor = IconWithColorWrapper(iconColor);

  if (!isHorizontal) {
    return (
      <Stack
        direction={"column"}
        // columnSpacing={isHorizontal ? 1 : 0}
        // rowSpacing={isHorizontal ? 0 : undefined}
        // wrap={"nowrap"}
        sx={{
          "& .lucide-chevron-down": {
            width: "16px",
            height: "16px",
            color: iconColor ?? theme.igIconColors.light,
          },
          // ...containerSx,
        }}
      >
        {label && (
          <Grid
            container
            direction={"row"}
            alignItems={"center"}
            columnSpacing={1}
            mt={0}
            size={12}
          >
            <Grid>
              <IgTypography {...labelProps}>
                {label}

                {required ? (
                  <IgTypography
                    {...labelProps}
                    color={theme.palette.text.tonal.error}
                  >
                    {" "}
                    *
                  </IgTypography>
                ) : (
                  ""
                )}
              </IgTypography>
            </Grid>
            <Grid mt={0} sx={{ minHeight: "20px" }}>
              {tooltipMessage && (
                <IgTooltip title={tooltipMessage} placement={tooltipPlacement}>
                  <Info size={16} />
                </IgTooltip>
              )}
            </Grid>
          </Grid>
        )}
        <Grid size={12}>
          <FormControl fullWidth={fullWidth}>
            <IgTooltip title={disabledTooltipMessage}>
              <Select<Value>
                sx={{
                  ...(!isHorizontal &&
                    label && {
                      marginTop: 1,
                    }),
                  ...sx,
                }}
                required={required}
                onChange={onChange}
                variant={variant}
                MenuProps={MenuProps}
                IconComponent={iconColor ? IconWithColor : IgIconComponent}
                fullWidth={fullWidth}
                {...rest}
              >
                {placeholder ? (
                  <MenuItem sx={{ display: "none" }} value={"ig-empty"}>
                    <IgTypography color={theme.palette.text.placeholder}>
                      {placeholder}
                    </IgTypography>
                  </MenuItem>
                ) : null}
                {children}
              </Select>
            </IgTooltip>
            {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
          </FormControl>
        </Grid>
      </Stack>
    );
  }

  return (
    <Grid
      container
      direction={"row"}
      columnSpacing={1}
      rowSpacing={0}
      wrap={"nowrap"}
      sx={{
        "& .lucide-chevron-down": {
          width: "16px",
          height: "16px",
          color: iconColor ?? theme.igIconColors.light,
        },
        ...containerSx,
      }}
    >
      {label && (
        <Grid
          container
          direction={"row"}
          alignItems={"center"}
          columnSpacing={1}
          mt={!helperText ? -1 : helperText ? `-${theme.spacing(7)}` : 0}
          size={"auto"}
        >
          <Grid>
            <IgTypography {...labelProps}>
              {label}

              {required ? (
                <IgTypography
                  {...labelProps}
                  color={theme.palette.text.tonal.error}
                >
                  {" "}
                  *
                </IgTypography>
              ) : (
                ""
              )}
            </IgTypography>
          </Grid>
          <Grid mt={1} sx={{ minHeight: "20px" }}>
            {tooltipMessage && (
              <IgTooltip title={tooltipMessage}>
                <Info size={16} />
              </IgTooltip>
            )}
          </Grid>
        </Grid>
      )}
      <Grid size={"auto"}>
        <FormControl fullWidth={fullWidth}>
          <IgTooltip title={disabledTooltipMessage}>
            <Select<Value>
              sx={{
                ...sx,
              }}
              required={required}
              onChange={onChange as unknown as SelectProps<Value>["onChange"]}
              variant={variant}
              MenuProps={MenuProps}
              IconComponent={iconColor ? IconWithColor : IgIconComponent}
              fullWidth={fullWidth}
              {...rest}
            >
              {placeholder ? (
                <MenuItem sx={{ display: "none" }} value={"ig-empty"}>
                  <IgTypography color={theme.palette.text.placeholder}>
                    {placeholder}
                  </IgTypography>
                </MenuItem>
              ) : null}
              {children}
            </Select>
          </IgTooltip>
          {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
        </FormControl>
      </Grid>
    </Grid>
  );
};
